import { ComboboxInput as HeadlessComboboxInput } from '@headlessui/react';
import { classNames } from '@src/utils/classNames';
import { type ForwardedRef, forwardRef, useEffect, useState } from 'react';

export type ComboboxInputProperties<Option> = {
  readonly displayValue: (value: Option) => string;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly onBlur?: () => void;
  readonly onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly value?: string;
  readonly testId?: string;
  readonly className?: string;
  readonly hasLeadingIcon?: boolean;
} & React.HTMLAttributes<HTMLInputElement>;

const ComboboxInputInner = <T,>(
  {
    testId,
    onBlur,
    displayValue,
    placeholder,
    className,
    disabled,
    hasLeadingIcon,
    onChange,
    value,
    ...rest
  }: ComboboxInputProperties<T>,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  const textBoxStyle = classNames(
    'w-full border-blue-100 border bg-white rounded-xl py-3',
    hasLeadingIcon ? 'pr-4 pl-12' : 'px-4',
    'focus-visible:outline-blue-500 focus-visible:border-blue-500 active:border-blue-500 focus:border-blue-500',
    disabled && 'cursor-not-allowed text-slate-500 !bg-zinc-700',
  );

  const [query, setQuery] = useState<string>(value ?? '');

  useEffect(() => setQuery(value ?? ''), [value]);
  return (
    <HeadlessComboboxInput
      className={classNames(className, textBoxStyle)}
      onChange={(event) => {
        setQuery(event.target.value);
        onChange?.(event);
      }}
      // @ts-expect-error The HeadlessComboboxInput is not as flexible as we are
      displayValue={displayValue}
      data-testid={testId ?? 'combobox-input'}
      value={query}
      placeholder={placeholder}
      onBlur={onBlur}
      ref={ref}
      {...rest}
    />
  );
};

export const ComboboxInput = forwardRef(ComboboxInputInner);
